import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, isTimeoutError, isNetworkError } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';
import { AlertTriangle, Trash2, Loader2, Link2, Unlink } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const DoctorSettings = () => {
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Delete account states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [apiError, setApiError] = useState(null);

  // Google linking states
  const [googleLinked, setGoogleLinked] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState(null);
  const [unlinkSuccess, setUnlinkSuccess] = useState(false);

  // Fetch Google linking status on mount
  useEffect(() => {
    const fetchGoogleStatus = async () => {
      if (!token) return;
      try {
        const response = await fetchWithTimeout(
          `${API_URL}/auth/me`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          },
          15000
        );
        if (response.ok) {
          const data = await response.json();
          setGoogleLinked(data.googleLinked || false);
          setHasPassword(data.hasPassword !== false);
        }
      } catch (error) {
        console.error('Error fetching Google status:', error);
      }
    };
    fetchGoogleStatus();
  }, [token]);

  // Handle Google account unlinking
  const handleUnlinkGoogle = useCallback(async () => {
    setIsUnlinking(true);
    setUnlinkError(null);
    setUnlinkSuccess(false);

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/auth/google/unlink`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.requiresPassword) {
          throw new Error('You must set a password before unlinking Google. Use the forgot password feature to create one.');
        }
        throw new Error(errorData.error || 'Failed to unlink Google account');
      }

      setGoogleLinked(false);
      setShowUnlinkConfirm(false);
      setUnlinkSuccess(true);
      setTimeout(() => setUnlinkSuccess(false), 3000);
    } catch (error) {
      console.error('Error unlinking Google:', error);
      if (isTimeoutError(error)) {
        setUnlinkError('Request timed out. Please try again.');
      } else if (isNetworkError(error)) {
        setUnlinkError('Unable to connect to the server. Please check your connection.');
      } else {
        setUnlinkError(error.message);
      }
    } finally {
      setIsUnlinking(false);
    }
  }, [token]);

  // Handle account deletion
  const handleDeleteAccount = useCallback(async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    setApiError(null);

    try {
      // First, get the doctor ID
      const meResponse = await fetchWithTimeout(
        `${API_URL}/auth/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (!meResponse.ok) {
        throw new Error('Failed to get user information');
      }

      const meData = await meResponse.json();
      const doctorId = meData.doctorId;

      if (!doctorId) {
        throw new Error('Doctor profile not found');
      }

      // Now delete the doctor account (this cascades to services, appointments, etc.)
      const deleteResponse = await fetchWithTimeout(
        `${API_URL}/doctors/${doctorId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        },
        30000 // Longer timeout for delete operation
      );

      if (!deleteResponse.ok) {
        const errorData = await deleteResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete account');
      }

      // Logout and redirect to home
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error deleting account:', error);
      setApiError(error);

      if (isTimeoutError(error)) {
        setDeleteError('Request timed out. Please try again.');
      } else if (isNetworkError(error)) {
        setDeleteError('Unable to connect to the server. Please check your connection.');
      } else {
        setDeleteError(error.message);
      }
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmText, token, logout, navigate]);

  // Retry handler
  const handleRetry = useCallback(() => {
    setApiError(null);
    setDeleteError(null);
  }, []);

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Account Settings
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            Manage your account preferences and settings
          </p>
        </div>

        {/* API Error Alert */}
        {apiError && (
          <ApiErrorAlert
            error={apiError}
            onRetry={handleRetry}
            isRetrying={false}
          />
        )}

        {/* Account Information */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Account Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Email
              </p>
              <p className="font-body">{user?.email}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Name
              </p>
              <p className="font-body">{user?.firstName} {user?.lastName}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Role
              </p>
              <p className="font-body capitalize">{user?.role}</p>
            </div>
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40 mb-1">
                Doctor ID
              </p>
              <p className="font-body font-mono text-xs">{user?.doctorId || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Google Account Linking */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4 flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Connected Accounts
          </h2>

          {/* Success message */}
          {unlinkSuccess && (
            <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-3 mb-4">
              <p className="font-body text-sm text-green-400">Google account unlinked successfully!</p>
            </div>
          )}

          <div className="flex items-center justify-between p-4 border border-light-border dark:border-border rounded-xl bg-light-surface dark:bg-obsidian/50">
            <div className="flex items-center gap-3">
              {/* Google Icon */}
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <p className="font-body font-medium">Google</p>
                <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                  {googleLinked ? 'Connected' : 'Not connected'}
                </p>
              </div>
            </div>

            {googleLinked ? (
              !showUnlinkConfirm ? (
                <button
                  onClick={() => setShowUnlinkConfirm(true)}
                  className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-red-500/50 hover:text-red-400 transition-smooth flex items-center gap-2"
                >
                  <Unlink className="w-4 h-4" />
                  Unlink
                </button>
              ) : null
            ) : (
              <span className="px-4 py-2 bg-light-surface dark:bg-charcoal border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                Not Linked
              </span>
            )}
          </div>

          {/* Unlink confirmation */}
          {showUnlinkConfirm && googleLinked && (
            <div className="mt-4 border border-amber-500/30 rounded-xl p-4 bg-amber-500/5">
              <p className="font-body text-sm text-amber-200 mb-3">
                Are you sure you want to unlink your Google account?
              </p>
              <p className="font-body text-xs text-light-text-secondary dark:text-silver/60 mb-4">
                You will no longer be able to sign in with Google. Make sure you have a password set for your account.
              </p>

              {unlinkError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 mb-3">
                  <p className="font-body text-sm text-red-400">{unlinkError}</p>
                </div>
              )}

              {!hasPassword && (
                <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg p-3 mb-3">
                  <p className="font-body text-sm text-amber-400">
                    Warning: You don't have a password set. Please use the "Forgot Password" feature to set one before unlinking.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleUnlinkGoogle}
                  disabled={isUnlinking}
                  className="px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-xl font-mono text-xs uppercase tracking-widest text-amber-300 hover:bg-amber-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isUnlinking ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Unlinking...
                    </>
                  ) : (
                    <>
                      <Unlink className="w-4 h-4" />
                      Confirm Unlink
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowUnlinkConfirm(false);
                    setUnlinkError(null);
                  }}
                  disabled={isUnlinking}
                  className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-300 dark:hover:border-white/20 transition-smooth disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="border border-red-500/30 rounded-2xl p-6 bg-red-500/5">
          <h2 className="font-mono text-sm uppercase tracking-widest text-red-400 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Danger Zone
          </h2>

          <div className="mb-4">
            <h3 className="font-body font-medium text-lg mb-2">Delete Account</h3>
            <p className="font-body text-light-text-secondary dark:text-silver/60 text-sm mb-4">
              Permanently delete your doctor account and all associated data. This action cannot be undone.
            </p>
            <p className="font-body text-slate-400 dark:text-silver/40 text-xs mb-4">
              <strong>This will delete:</strong>
            </p>
            <ul className="list-disc list-inside font-body text-slate-400 dark:text-silver/40 text-xs mb-4 space-y-1">
              <li>Your doctor profile and bio</li>
              <li>All services you've created</li>
              <li>All appointments (past and future)</li>
              <li>All reviews associated with your account</li>
              <li>Your availability settings</li>
              <li>Your user account</li>
            </ul>
          </div>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-xl font-mono text-xs uppercase tracking-widest text-red-400 hover:bg-red-500/30 transition-smooth flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete My Account
            </button>
          ) : (
            <div className="border border-red-500/30 rounded-xl p-4 bg-red-500/10">
              <p className="font-body text-sm text-red-400 mb-3">
                Are you absolutely sure? Type <strong>DELETE</strong> to confirm:
              </p>

              {deleteError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 mb-3">
                  <p className="font-body text-sm text-red-400">{deleteError}</p>
                </div>
              )}

              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full px-4 py-2 bg-obsidian border border-red-500/30 rounded-lg font-mono text-sm mb-3 focus:outline-none focus:border-red-500"
                disabled={isDeleting}
              />

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmText !== 'DELETE'}
                  className="px-4 py-2 bg-red-500 rounded-xl font-mono text-xs uppercase tracking-widest text-white hover:bg-red-600 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Permanently Delete
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                    setDeleteError(null);
                  }}
                  disabled={isDeleting}
                  className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest hover:border-slate-300 dark:hover:border-white/20 transition-smooth disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Back to Dashboard Link */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/doctor/dashboard')}
            className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-white transition-smooth"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorSettings;
